import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { FileText, ShieldCheck, AlertTriangle, FilePenLine, FileSearch, Calendar } from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'dpdp-aligned', label: 'DPDP-aligned' },
  { key: 'needs-update', label: 'Needs Update' },
  { key: 'review', label: 'In Review' },
  { key: 'draft', label: 'Draft' }
];

const STATUS_COLOR = {
  'dpdp-aligned': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'needs-update': 'bg-amber-100 text-amber-800 border-amber-200',
  review: 'bg-blue-100 text-blue-700 border-blue-200',
  draft: 'bg-gray-100 text-gray-700 border-gray-200'
};

const ContractsHub = () => {
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusF, setStatusF] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/contracts`).then((r) => setContracts(r.data)).catch(() => setContracts([]));
    axios.get(`${API}/contracts/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const filtered = useMemo(() => {
    return contracts
      .filter((c) => (statusF === 'all' ? true : c.status === statusF))
      .filter((c) => {
        const s = q.trim().toLowerCase();
        if (!s) return true;
        return [c.vendor, c.category, c.summary, c.contract_ref].join(' ').toLowerCase().includes(s);
      });
  }, [contracts, statusF, q]);

  const overdue = (stats?.days_to_deadline ?? 0) < 0;
  const deadlineLabel = overdue
    ? `Overdue by ${Math.abs(stats?.days_to_deadline)} days`
    : `${stats?.days_to_deadline ?? '—'} days left`;

  return (
    <EmbeddedModule
      eyebrow="RBI CMS · 10-Apr-2026"
      title="Documentation Hub — Contract Management System"
      tagline="Enterprise CMS for vendor contracts. DPDP-aligned, RBI-IT-Outsourcing-aligned, IT-stack ready (frontend + backend) — drop-in deployable for any bank, NBFC or fintech."
      badgeText={overdue ? `Deadline: ${stats?.rbi_cms_deadline} · Overdue` : `Deadline: ${stats?.rbi_cms_deadline}`}
      accentClass="from-slate-900 via-blue-900 to-indigo-900"
      testId="contracts-hub-page"
      kpis={[
        { label: 'Contracts', value: stats?.total ?? '—' },
        { label: 'RBI-CMS Ready', value: stats?.ready ?? '—' },
        { label: 'Readiness', value: stats ? `${stats.readiness_score}%` : '—' },
        { label: 'Deadline', value: deadlineLabel }
      ]}
    >
      {/* Readiness summary */}
      <Card data-testid="cms-readiness" className={overdue ? 'border-rose-300' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center space-x-2">
              <Calendar className={`w-5 h-5 ${overdue ? 'text-rose-600' : 'text-blue-700'}`} />
              <span>RBI CMS Readiness — 10-Apr-2026</span>
            </span>
            <Badge className={overdue ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}>
              {deadlineLabel}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Progress value={stats?.readiness_score ?? 0} className="h-2" />
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats?.by_status || {}).map(([k, v]) => (
                <Badge key={k} variant="outline" className={`text-[11px] ${STATUS_COLOR[k] || ''}`}>
                  {k} · {v}
                </Badge>
              ))}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1.5">Standard Nomenclature Clauses</p>
              <div className="flex flex-wrap gap-1.5">
                {(stats?.standard_clauses || []).map((c) => (
                  <Badge key={c} variant="secondary" className="text-[10px] font-mono">{c}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contract list */}
      <Card data-testid="contracts-list">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-700" />
              <span>Vendor Contract Repository</span>
              <Badge variant="secondary">{filtered.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search vendor / category / ref..."
                className="h-9 w-72"
                data-testid="contracts-search"
              />
              <div className="flex items-center gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`status-filter-${f.key}`}
                    onClick={() => setStatusF(f.key)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      statusF === f.key
                        ? 'bg-blue-700 text-white border-blue-700'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`contract-${c.contract_ref}`}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="text-[10px] font-mono">{c.contract_ref}</Badge>
                      <Badge className={`text-[10px] border ${STATUS_COLOR[c.status] || ''}`}>{c.status}</Badge>
                      {c.rbi_cms_ready ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] inline-flex items-center">
                          <ShieldCheck className="w-3 h-3 mr-0.5" />RBI CMS Ready
                        </Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 text-[10px] inline-flex items-center">
                          <AlertTriangle className="w-3 h-3 mr-0.5" />Action Needed
                        </Badge>
                      )}
                      <span className="text-[11px] text-gray-500">{c.value_inr}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{c.vendor}</p>
                    <p className="text-xs text-gray-600">{c.category} · {c.doc_type} · Owner: {c.owner}</p>
                    <p className="text-xs text-gray-700 mt-2">{c.summary}</p>
                    {c.dpdp_clauses?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.dpdp_clauses.map((cl) => (
                          <Badge key={cl} variant="secondary" className="text-[10px] font-mono">{cl}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] text-gray-500">Expires</p>
                    <p className="text-xs font-semibold text-gray-800">{c.expiry || '—'}</p>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No contracts match.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default ContractsHub;
